import React, { useState, useEffect } from 'react';
import { ShieldCheck, Truck, Clock, RefreshCw, BadgeCheck } from 'lucide-react';
import { cn } from '@/lib/utils'; // Assuming utils exist

// Component Props Interface
export interface TrustUrgencyProps {
    mode: 'trust' | 'stock' | 'timer' | 'mixed';
    badges: string[];
    stockLevel?: number;
    timerDurationMinutes?: number;
    accentColor?: string;
    textColor?: string;
    backgroundColor?: string;
    className?: string; // For Puck editor wrapper
}

// Icon Mapping
const ICON_MAP: Record<string, React.ReactNode> = {
    'cod': <span className="font-bold text-xs" style={{ fontFamily: 'sans-serif' }}>COD</span>, // Simple text or specific icon
    'shipping': <Truck className="w-5 h-5" />,
    'guarantee': <ShieldCheck className="w-5 h-5" />,
    'return': <RefreshCw className="w-5 h-5" />,
    'authentic': <BadgeCheck className="w-5 h-5" />,
    'support': <Clock className="w-5 h-5" />,
};

const LABEL_MAP: Record<string, string> = {
    'cod': 'الدفع عند الاستلام',
    'shipping': 'توصيل سريع 58 ولاية',
    'guarantee': 'ضمان استبدال 7 أيام',
    'return': 'استرجاع مجاني',
    'authentic': 'منتج أصلي 100%',
    'support': 'دعم فني 7/7',
};

import { usePageContext } from '@/components/ecoBuilder/renderer/PageContext';

export const TrustUrgency: React.FC<TrustUrgencyProps> = ({
    mode = 'trust',
    badges = ['cod', 'shipping', 'guarantee'],
    stockLevel = 5,
    timerDurationMinutes = 15,
    accentColor,
    textColor,
    backgroundColor,
    className
}) => {
    const { themeSettings } = usePageContext();

    // Resolve Colors
    const primaryColor = themeSettings?.primaryColor || '#f59e0b';
    const resolvedAccentColor = accentColor || primaryColor;
    const resolvedTextColor = textColor === 'foreground' || !textColor ? (themeSettings?.textColor || 'inherit') : textColor;
    const resolvedBackgroundColor = backgroundColor === 'background' || !backgroundColor ? 'transparent' : backgroundColor;

    const [timeLeft, setTimeLeft] = useState(timerDurationMinutes * 60);

    // Normalize badges to always be an array to prevent "map is not a function" error
    // The editor might pass a single string from a Select field, or an array from a MultiSelect
    const safeBadges = (() => {
        if (Array.isArray(badges)) return badges;
        if (typeof badges === 'string') return [badges];
        return ['cod', 'shipping', 'guarantee']; // Default fallback
    })();

    // Timer Logic
    useEffect(() => {
        if (mode !== 'timer' && mode !== 'mixed') return;

        // Try to get from localStorage to persist across reloads
        const savedEndTime = localStorage.getItem('urgency_timer_end');
        let endTime = savedEndTime ? parseInt(savedEndTime, 10) : 0;
        const now = Date.now();

        if (!endTime || endTime < now) {
            endTime = now + timerDurationMinutes * 60 * 1000;
            localStorage.setItem('urgency_timer_end', endTime.toString());
        }

        const interval = setInterval(() => {
            const currentNow = Date.now();
            const diff = Math.max(0, Math.floor((endTime - currentNow) / 1000));
            setTimeLeft(diff);

            if (diff === 0) {
                // Timer finished
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [mode, timerDurationMinutes]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // Style Helpers
    const containerStyle = {
        backgroundColor: resolvedBackgroundColor,
        color: resolvedTextColor,
    };

    // Classes
    const baseClasses = "w-full py-4 px-4 flex flex-wrap items-center justify-center gap-4 text-center select-none";

    return (
        <div className={cn(baseClasses, className)} style={containerStyle} dir="rtl">

            {/* MODE: TRUST BADGES */}
            {(mode === 'trust' || mode === 'mixed') && (
                <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                    {safeBadges.map((badge, idx) => (
                        <div key={`${badge}-${idx}`} className="flex flex-col items-center gap-2 group cursor-default">
                            <div className="p-3 rounded-full bg-primary/10 text-primary transition-transform group-hover:scale-110" style={{ color: resolvedAccentColor, backgroundColor: `${resolvedAccentColor}20` }}>
                                {ICON_MAP[badge] || <BadgeCheck className="w-5 h-5" />}
                            </div>
                            <span className="text-xs font-semibold opacity-90">{LABEL_MAP[badge] || badge}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* SEPARATOR for mixed mode */}
            {mode === 'mixed' && <div className="hidden md:block w-px h-10 bg-border mx-4"></div>}

            {/* MODE: STOCK COUTNER */}
            {(mode === 'stock' || mode === 'mixed') && (
                <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 px-4 py-2 rounded-xl text-red-600 animate-pulse">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                    <span className="font-bold text-sm">
                        متبقي {stockLevel} قطع فقط في المخزون!
                    </span>
                </div>
            )}

            {/* MODE: TIMER */}
            {(mode === 'timer' || mode === 'mixed') && (
                <div className="flex flex-col items-center">
                    <span className="text-xs mb-1 opacity-70">ينتهي العرض خلال:</span>
                    <div className="flex items-center gap-1 text-xl font-mono font-bold" style={{ color: resolvedAccentColor }}>
                        <Clock className="w-5 h-5 mr-1" />
                        {formatTime(timeLeft)}
                    </div>
                </div>
            )}
        </div>
    );
};

// ... existing code ...

// export default TrustUrgency; // Removed to fix build error

// ═══════════════════════════════════════════════════════════════
// EDITOR CONFIGURATION
// ═══════════════════════════════════════════════════════════════

export const TrustUrgencyDefaultProps: Partial<TrustUrgencyProps> = {
    mode: 'trust',
    badges: ['cod', 'shipping', 'guarantee'],
    stockLevel: 5,
    timerDurationMinutes: 15,
    accentColor: '#f59e0b',
    textColor: 'foreground',
    backgroundColor: 'background'
};

export const TrustUrgencySchema: any[] = [
    {
        key: 'mode',
        label: 'الوضع (Mode)',
        type: 'select',
        options: [
            { value: 'trust', label: 'شارات الثقة فقط' },
            { value: 'stock', label: 'عداد المخزون (Scarcity)' },
            { value: 'timer', label: 'عداد الوقت (Urgency)' },
            { value: 'mixed', label: 'مختلط (الكل)' }
        ]
    },
    {
        key: 'badges',
        label: 'الشارات المعروضة',
        type: 'select', // Ideally should be multi-select, but simple select for now or custom implementation later
        // In simple Puck select, we can only pick one. For multi-select simulation, we might need a custom field or just a boolean per badge.
        // Let's stick to simple setup for MVP or define standard options if we assume 'badges' is array.
        // If EditorFieldType doesn't support multiselect, use text comma separated or similar? 
        // Component expects array. Let's try 'select' assuming pUCK might handle array or we change comp prop to string.
        // Actually, let's keep it simple: predefined sets or just text input for IDs?
        // Let's use checkboxes if available, or just keeping the default set for MVP and allowing Mode toggle.
        // I'll leave badges config minimal for now.
        options: [
            { value: 'cod', label: 'دفع عند الاستلام' },
            { value: 'shipping', label: 'توصيل سريع' },
            { value: 'guarantee', label: 'ضمان' }
        ]
        // Note: Real multiselect needs better field type
    },
    {
        key: 'stockLevel',
        label: 'مستوى المخزون (الوهمي)',
        type: 'number',
        defaultValue: 5
    },
    {
        key: 'timerDurationMinutes',
        label: 'مدة العداد (دقيقة)',
        type: 'number',
        defaultValue: 15
    },
    {
        key: 'accentColor',
        label: 'لون التمييز (الأيقونات/العداد)',
        type: 'color',
        defaultValue: '#f59e0b'
    },
    {
        key: 'backgroundColor',
        label: 'لون الخلفية',
        type: 'color'
    }
];

